import type { ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  className?: string;
}

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };

/** Generic, presentation-only table. Data fetching belongs to TanStack Query. */
export function Table<T>({ columns, data, rowKey, onRowClick, empty, className }: TableProps<T>) {
  return (
    <div className={cn('overflow-x-auto rounded-card border border-outline', className)}>
      <table className="w-full border-collapse text-data-md">
        <thead>
          <tr className="border-b border-outline bg-surface-container-low">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'px-4 py-3 text-label-caps font-semibold uppercase text-on-surface-variant',
                  ALIGN[c.align ?? 'left'],
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-on-surface-variant">
                {empty ?? 'No records.'}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-outline last:border-0 bg-surface transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-surface-variant',
                )}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-4 py-3 text-on-surface', ALIGN[c.align ?? 'left'], c.align === 'right' && 'tabular-nums', c.className)}>
                    {c.render ? c.render(row) : (row as Record<string, ReactNode>)[c.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
